import { EnumInfoDelegaTipo } from '@framework/api-client';
import { FormItem, SelectInput, SwitchInput, useField } from '@framework/form';
import { IDelegation } from '@framework/frontoffice-submission';
import { useTranslation } from '@framework/i18n';
import { RowElementItem } from '@framework/ui';
import { Button, Col, Row, Space } from 'antd';
import React from 'react';
import { useWatch } from 'react-hook-form';
import { DelegationTypeUserTag } from './DelegationTypeUserTag';
import { EditModulesDelegationsValues } from './EditModulesDelegationsSchema';

import AddressBookButton from '#build-flags/routes/address-book/button/AddressBookButton';

export interface IModulesViewDelegationsInputProps {
  name: string;
  label: string;
  uniqueDelegations: IDelegation[];
  needConfirm: boolean;
}

const TFormItem = FormItem as typeof FormItem<EditModulesDelegationsValues>;

/**
 * Componente di input per la gestione dei dati di delega di visualizzazione.
 * Se i moduli forniti hanno delegati differenti, mostra una maschera
 * che richiede la conferma esplicita per la modifica.
 *
 * Permette l'inserimento di una lista di delegati
 */
export function ModulesViewDelegationsInput(
  props: IModulesViewDelegationsInputProps,
) {
  const { t } = useTranslation();
  const { name, label, uniqueDelegations, needConfirm } = props;

  const enable = useWatch({ name: `view.enable` });
  const isConfirmed = useWatch({ name: `view.confirmUpdate` });
  const confirmField = useField(`view.confirmUpdate`);

  const helpText = t(
    'inserisci-codici-fiscali-o-email-delegati',
    'Inserisci i Codici Fiscali o le E-Mail per gli utenti esteri',
  );

  const selected = useWatch({ name: 'view.delegate' });

  return (
    <Row>
      <Col span={10}>
        <Space>
          <TFormItem
            name="view.enable"
            label={t(
              'modules-delegations.view.abilita-deleghe',
              'Abilita deleghe',
            )}
            layout={{ removeHelpMargin: true, hideVisualLabel: true }}
          >
            <SwitchInput name="view.enable">
              <DelegationTypeUserTag
                delegate={{
                  type: EnumInfoDelegaTipo.VISUALIZZAZIONE,
                  userName: label,
                }}
              />
            </SwitchInput>
          </TFormItem>
        </Space>
      </Col>
      <Col span={14}>
        {enable &&
          (needConfirm ? (
            <>
              <RowElementItem colSpan={4}>
                <TFormItem name="view.delegate" label={label} help={helpText}>
                  <Space.Compact size="large" block>
                    <SelectInput
                      name="view.delegate"
                      disabled={!isConfirmed}
                      placeholder={
                        !isConfirmed
                          ? t(
                              'delegati-differenti-o-mancanti',
                              'I moduli presentano delegati differenti o mancanti',
                            )
                          : undefined
                      }
                      mode="tags"
                    />
                    {AddressBookButton && (
                      <AddressBookButton
                        idFieldName="view.delegate"
                        onSelect={(record, close, setValue) => {
                          setValue((ids: string[]) => [
                            ...new Set([
                              ...ids,
                              record.codiceFiscale ?? record.email,
                            ]),
                          ]);
                        }}
                        selected={selected}
                      />
                    )}
                  </Space.Compact>
                </TFormItem>
              </RowElementItem>

              <Button
                block
                size="large"
                type={isConfirmed ? 'default' : 'primary'}
                onClick={() => confirmField.setValue(!isConfirmed)}
              >
                {isConfirmed
                  ? t('annulla-modifiche', 'Annulla modifiche')
                  : t('modifica-tutti', 'Modifica tutti')}
              </Button>

              {/* <ModulesPreviousDelegationsSummary
                delegations={uniqueDelegations}
              /> */}
            </>
          ) : (
            <TFormItem name="view.delegate" label={label} help={helpText}>
              <Space.Compact size="large" block>
                <SelectInput name="view.delegate" mode="tags" />
                {AddressBookButton && (
                  <AddressBookButton
                    idFieldName="view.delegate"
                    onSelect={(record, close, setValue) => {
                      setValue((ids: string[]) => [
                        ...new Set([
                          ...ids,
                          record.codiceFiscale ?? record.email,
                        ]),
                      ]);
                    }}
                    selected={selected}
                  />
                )}
              </Space.Compact>
            </TFormItem>
          ))}
      </Col>
    </Row>
  );
}
